import { t } from 'i18next';
import React, { RefObject, useEffect, useImperativeHandle, useRef } from 'react';
import './styles.scss';

interface PlaceholderAnimationOptions {
	delayAfterAnimation: number;
	value?: string;
	className?: string;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onClick?: () => void;
	cyTestRef?: string;
	autoFocus?: boolean;
	isInputFieldRequired?: boolean;
	onBlur?: any;
}

const AnimatedPlaceholder = React.forwardRef<RefObject<HTMLInputElement>, PlaceholderAnimationOptions>(
	({ value, delayAfterAnimation, className, onChange, onFocus, onClick, cyTestRef, autoFocus, onBlur, isInputFieldRequired }, ref) => {
		const currentPlaceholderIndex = useRef<number>(0);
		const placeholders = [t('legiaWarszawa'), t('tysonFury'), t('manchesterUnited'), t('igaSwiatek')];
		const searchBarRef = useRef<HTMLInputElement>(null);

		let animationTimeout: NodeJS.Timeout;

		const animatePlaceholder = () => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const currentPlaceholder = placeholders[currentPlaceholderIndex.current]!;
			if (!searchBarRef.current) return;

			let index = 0;
			animationTimeout = setInterval(() => {
				if (index === currentPlaceholder.length) {
					clearInterval(animationTimeout);
					placeholderAnimation();
					return;
				}

				if (isInputFieldRequired && searchBarRef.current) {
					searchBarRef.current.textContent = currentPlaceholder.substring(0, index + 1);
				} else if (searchBarRef.current) {
					searchBarRef.current.setAttribute('placeholder', currentPlaceholder.substring(0, index + 1));
				}

				index++;
			}, 100);
		};

		const placeholderAnimation = () => {
			animationTimeout = setTimeout(() => {
				currentPlaceholderIndex.current = (currentPlaceholderIndex.current + 1) % placeholders.length;
				animatePlaceholder();
			}, delayAfterAnimation);
		};
		useImperativeHandle(ref, () => searchBarRef);
		useEffect(() => {
			if (!value) {
				animatePlaceholder();
			}

			return () => {
				clearTimeout(animationTimeout);
				clearInterval(animationTimeout);
			};
		}, [delayAfterAnimation, placeholders, value]);

		const renderClassname = !className ? `AnimatedPlaceholder__input` : `${className}`;

		return (
			<>
				{!isInputFieldRequired ? (
					<input
						type="text"
						value={value}
						className={renderClassname}
						ref={searchBarRef}
						onChange={onChange}
						onFocus={onFocus}
						onClick={onClick}
						data-cy={cyTestRef}
						autoFocus={autoFocus}
						onBlur={onBlur}
					/>
				) : (
					<div className={renderClassname} ref={searchBarRef} data-cy={cyTestRef} />
				)}
			</>
		);
	},
);

export default AnimatedPlaceholder;
